import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import MapInstantStart from '../components/MapInstantStart';
import ListingCard from '../components/ListingCard';
import ClassifiedCard from '../components/ClassifiedCard';
import SmartSearch from '../components/SmartSearch';
import { Search, Store, Briefcase, Calendar, ArrowRight, BookOpen, Star, MapPin, Clock, CheckCircle2, UtensilsCrossed, Ticket } from 'lucide-react';
import { resolveAssetUrl } from '../lib/assets';
import { EVENT_CATEGORIES } from '../lib/eventData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIVITY_LABELS = {
  eat: { lv: 'Paēst & Izbaudīt', en: 'Eat & Enjoy', ru: 'Поесть и насладиться', color: '#FF9F1C', Icon: UtensilsCrossed },
  entertainment: { lv: 'Izklaide & Atpūta', en: 'Entertainment', ru: 'Развлечения и отдых', color: '#EC4899', Icon: Calendar },
  shop: { lv: 'Iepirkties', en: 'Shopping', ru: 'Покупки', color: '#2563EB', Icon: Store },
  service: { lv: 'Pakalpojumi', en: 'Services', ru: 'Услуги', color: '#4A6B53', Icon: Briefcase },
};

const pick = (lang, lv, en, ru) => (lang === 'lv' ? lv : lang === 'ru' ? (ru ?? en) : en);

function BizResultCard({ b, language, navigate }) {
  const meta = ACTIVITY_LABELS[b.primary] || ACTIVITY_LABELS.shop;
  const Icon = meta.Icon;
  const dest = b.template ? `/uznemums/${b.id}` : `/shop/${b.id}`;
  return (
    <div className="bg-white rounded-2xl border border-[#E5E4E0] p-4 flex flex-col hover:shadow-md transition-shadow" data-testid={`biz-result-${b.id}`}>
      <div className="flex items-start gap-3 mb-3">
        {b.logo ? (
          <img src={resolveAssetUrl(b.logo)} alt="" className="w-12 h-12 rounded-xl object-cover shrink-0" />
        ) : (
          <span className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${meta.color}1A`, color: meta.color }}>
            <Icon className="w-6 h-6" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-outfit font-semibold text-[#121212] text-sm truncate flex items-center gap-1">
            {b.name}
            {b.verified && <CheckCircle2 className="w-3.5 h-3.5 text-[#2563EB] shrink-0" />}
          </p>
          <p className="text-xs mt-0.5 flex items-center gap-1" style={{ color: meta.color }}>
            <Icon className="w-3 h-3" /> {pick(language, meta.lv, meta.en, meta.ru)}
          </p>
          {(b.location_name || b.region) && (
            <p className="text-[11px] text-[#9CA3AF] mt-0.5 flex items-center gap-0.5 truncate">
              <MapPin className="w-3 h-3 shrink-0" /> {b.location_name || b.region}
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3 text-[11px]">
        {typeof b.rating === 'number' && (
          <span className="flex items-center gap-0.5 font-semibold text-[#F59E0B]"><Star className="w-3 h-3 fill-current" /> {b.rating}</span>
        )}
        <span className={`flex items-center gap-0.5 font-medium ${b.open_now ? 'text-[#16A34A]' : 'text-[#9CA3AF]'}`}>
          <Clock className="w-3 h-3" /> {b.open_now ? pick(language, 'Atvērts', 'Open', 'Открыто') : pick(language, 'Slēgts', 'Closed', 'Закрыто')}
        </span>
        {b.has_free_slots && (
          <span className="flex items-center gap-0.5 font-medium text-[#EC4899]">
            <Calendar className="w-3 h-3" /> {pick(language, 'Brīvi laiki', 'Free slots', 'Свободное время')}
          </span>
        )}
      </div>
      <button onClick={() => navigate(dest)}
        className="mt-auto w-full h-9 rounded-full text-white text-xs font-semibold flex items-center justify-center gap-1 hover:opacity-90 transition-opacity"
        style={{ background: meta.color }}
        data-testid={`biz-result-open-${b.id}`}>
        {pick(language, 'Atvērt', 'Open', 'Открыть')} <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

const EVENTS_COLOR = '#E11D48';

function EventResultCard({ ev, language, navigate }) {
  const isPaid = ev.is_paid === true || ev.is_paid === 'true';
  const cat = EVENT_CATEGORIES[ev.category];
  const catLabel = cat ? pick(language, cat.lv, cat.en, cat.ru) : null;
  const when = ev.event_date && ev.event_date !== 'null'
    ? new Date(ev.event_date).toLocaleDateString(language === 'lv' ? 'lv-LV' : language === 'ru' ? 'ru-RU' : 'en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : null;
  const img = ev.image && ev.image !== 'null' ? resolveAssetUrl(ev.image) : null;
  return (
    <div className="bg-white rounded-2xl border border-[#E5E4E0] overflow-hidden flex flex-col hover:shadow-md transition-shadow" data-testid={`event-result-${ev.id}`}>
      {img ? (
        <img src={img} alt="" className="w-full h-32 object-cover" />
      ) : (
        <div className="w-full h-32 flex items-center justify-center" style={{ background: `${EVENTS_COLOR}12` }}>
          <Calendar className="w-8 h-8" style={{ color: EVENTS_COLOR }} />
        </div>
      )}
      <div className="p-4 flex flex-col flex-1">
        <p className="font-outfit font-semibold text-[#121212] text-sm line-clamp-2 mb-1">{ev.name}</p>
        {when && (
          <p className="text-xs text-[#5C5C5C] flex items-center gap-1 mb-0.5">
            <Clock className="w-3 h-3 shrink-0" /> {when}
          </p>
        )}
        {ev.location_name && ev.location_name !== 'null' && (
          <p className="text-[11px] text-[#9CA3AF] flex items-center gap-1 truncate mb-2">
            <MapPin className="w-3 h-3 shrink-0" /> {ev.location_name}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2 mb-3 text-[11px]">
          {catLabel && (
            <span className="px-2 py-0.5 rounded-full font-medium" style={{ background: `${EVENTS_COLOR}12`, color: EVENTS_COLOR }}>{catLabel}</span>
          )}
          {isPaid ? (
            <span className="flex items-center gap-0.5 font-semibold" style={{ color: EVENTS_COLOR }}>
              <Ticket className="w-3 h-3" /> {ev.price && ev.price !== 'null' ? `€${ev.price}` : pick(language, 'Maksas', 'Paid', 'Платно')}
            </span>
          ) : (
            <span className="flex items-center gap-0.5 font-semibold text-[#16A34A]">
              <Ticket className="w-3 h-3" /> {pick(language, 'Bezmaksas', 'Free', 'Бесплатно')}
            </span>
          )}
        </div>
        <button onClick={() => navigate(`/events/${ev.id}`)}
          className="mt-auto w-full h-9 rounded-full text-white text-xs font-semibold flex items-center justify-center gap-1 hover:opacity-90 transition-opacity"
          style={{ background: EVENTS_COLOR }}
          data-testid={`event-result-open-${ev.id}`}>
          {pick(language, 'Skatīt pasākumu', 'View event', 'Смотреть событие')} <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function HomePage() {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [featuredListings, setFeaturedListings] = useState([]);
  const [featuredClassifieds, setFeaturedClassifieds] = useState([]);
  const [seasonData, setSeasonData] = useState(null);
  const [taxonomy, setTaxonomy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bizResults, setBizResults] = useState({ list: [], active: false });
  const [eventResults, setEventResults] = useState({ list: [], active: false });
  const handleBizResults = useCallback((list, active, type) => {
    if (type === 'events') setEventResults({ list: list || [], active });
    else setBizResults({ list: list || [], active });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const listingsRes = await axios.get(`${API}/listings`, { params: { limit: 8 } });
        setFeaturedListings(listingsRes.data);
        axios.get(`${API}/classifieds`, { params: { limit: 8 } }).then(({ data }) => setFeaturedClassifieds(data || [])).catch(() => {});
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchSeasonData = async () => {
      try {
        const { data } = await axios.get(`${API}/catalog/current-season`);
        setSeasonData(data);
      } catch (error) {
        console.error('Error fetching season data:', error);
      }
    };
    fetchSeasonData();
    axios.get(`${API}/categories`).then(({ data }) => setTaxonomy(data.taxonomy)).catch(() => {});
  }, []);


  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-[#F3F2EE] to-[#FAFAFA] py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-[#121212] mb-6 tracking-tight">
              {t('heroTitle')}
            </h1>
            <p className="text-lg sm:text-xl text-[#5C5C5C] font-figtree mb-8">
              {t('heroSubtitle')}
            </p>
            
            {/* Smart Search */}
            <SmartSearch taxonomy={taxonomy} />
          </div>

          {/* Map - Full Width */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-outfit text-lg font-semibold text-[#121212]">
                {pick(language, 'Atrodi tuvākos pārdevējus', 'Find sellers near you', 'Найдите продавцов рядом')}
              </h2>
              <Link to="/karte" className="flex items-center gap-1 text-sm font-semibold text-[#2563EB] hover:text-[#1d4fd7] transition-colors" data-testid="home-view-full-map">
                {pick(language, 'Skatīt visu karti', 'View the full map', 'Открыть всю карту')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <MapInstantStart height="460px" rememberViewKey="kolv-map-home" data-testid="home-map"
              linkToFullMap onResultsChange={handleBizResults} />

            {/* Business results list — mirrors the map's "Uzņēmumi" filters */}
            {bizResults.active && (
              <div className="mt-4" data-testid="biz-results-list">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-outfit text-base font-semibold text-[#121212]">
                    {pick(language, 'Uzņēmumi kartē', 'Businesses on the map', 'Компании на карте')}
                    <span className="ml-2 text-sm font-normal text-[#5C5C5C]">({bizResults.list.length})</span>
                  </h3>
                </div>
                {bizResults.list.length === 0 ? (
                  <p className="text-sm text-[#5C5C5C] py-6 text-center bg-white rounded-2xl border border-[#E5E4E0]" data-testid="biz-results-empty">
                    {pick(language, 'Nav atrasts neviens uzņēmums ar šiem filtriem.', 'No businesses match these filters.', 'Нет компаний, соответствующих этим фильтрам.')}
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {bizResults.list.map((b) => (
                      <BizResultCard key={b.id} b={b} language={language} navigate={navigate} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Event results list — mirrors the map's "Pasākumi" filters */}
            {eventResults.active && (
              <div className="mt-4" data-testid="event-results-list">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-outfit text-base font-semibold text-[#121212]">
                    {pick(language, 'Pasākumi kartē', 'Events on the map', 'События на карте')}
                    <span className="ml-2 text-sm font-normal text-[#5C5C5C]">({eventResults.list.length})</span>
                  </h3>
                </div>
                {eventResults.list.length === 0 ? (
                  <p className="text-sm text-[#5C5C5C] py-6 text-center bg-white rounded-2xl border border-[#E5E4E0]" data-testid="event-results-empty">
                    {pick(language, 'Nav atrasts neviens pasākums ar šiem filtriem.', 'No events match these filters.', 'Нет событий, соответствующих этим фильтрам.')}
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {eventResults.list.map((ev) => (
                      <EventResultCard key={ev.id} ev={ev} language={language} navigate={navigate} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Seasonal Catalog Section */}
      {seasonData && (
        <section className="py-16 bg-[#FFF8F0]" data-testid="season-catalog-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-[#D97736]/10 rounded-xl flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-[#D97736]" />
                  </div>
                  <h2 className="font-outfit text-2xl sm:text-3xl font-bold text-[#121212]">
                    {pick(language, 'Sezonas katalogs', 'Seasonal Catalog', 'Сезонный каталог')}
                  </h2>
                </div>
                <p className="text-[#5C5C5C]">
                  {language === 'lv' ? seasonData.season_name_lv : seasonData.season_name_en}
                </p>
              </div>
              <Link
                to="/catalog"
                className="flex items-center gap-1 text-[#D97736] hover:text-[#C06528] font-medium transition-colors"
                data-testid="view-all-catalogs-link"
              >
                {pick(language, 'Visi katalogi', 'All catalogs', 'Все каталоги')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {seasonData.all_listings.length > 0 ? (
              <>
                {/* Catalog filter tabs */}
                {Object.keys(seasonData.catalog_groups).length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {Object.entries(seasonData.catalog_groups).map(([catId, group]) => (
                      <Link
                        key={catId}
                        to={`/listings?catalog=${catId}`}
                        className="px-4 py-2 bg-white border border-[#D97736]/30 rounded-xl text-sm text-[#121212] hover:bg-[#D97736] hover:text-white transition-colors font-medium"
                        data-testid={`season-tab-${catId}`}
                      >
                        {language === 'lv' ? group.name_lv : group.name_en}
                        <span className="ml-1.5 text-xs opacity-70">({group.listings.length})</span>
                      </Link>
                    ))}
                  </div>
                )}

                {/* Listings grid */}
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {seasonData.all_listings.slice(0, 8).map((listing) => (
                    <ListingCard key={listing.id} listing={listing} />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12 bg-white/50 rounded-2xl border border-[#D97736]/10">
                <BookOpen className="w-12 h-12 mx-auto mb-3 text-[#D97736]/40" />
                <p className="text-[#5C5C5C] mb-1">
                  {pick(language,
                    'Pagaidām nav preču šīs sezonas katalogos',
                    "No products in this season's catalogs yet",
                    'Пока нет товаров в каталогах этого сезона')}
                </p>
                <p className="text-sm text-[#5C5C5C]/70">
                  {pick(language,
                    'Pievienojiet savas preces katalogam, veidojot sludinājumu',
                    'Add your products to catalogs when creating a listing',
                    'Добавьте свои товары в каталог при создании объявления')}
                </p>
              </div>
            )}
          </div>
        </section>
      )}


      {/* Featured Listings */}
      <section className="py-16 bg-[#F3F2EE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-outfit text-2xl sm:text-3xl font-bold text-[#121212]">
              {t('featuredListings')}
            </h2>
            <Link
              to="/listings"
              className="flex items-center gap-1 text-[#D97736] hover:text-[#C06528] font-medium transition-colors"
              data-testid="view-all-link"
            >
              {t('viewAll')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl h-72 animate-pulse" />
              ))}
            </div>
          ) : featuredListings.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredListings.slice(0, 8).map((listing) => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/efa94e61-0f93-4547-8f4c-a14677aa5a5f/images/25c019ba83ef8525a012ae8263ca3496e085d57152479e9b3cfe13aa9ae31c0c.png"
                alt="No results"
                className="w-48 h-48 mx-auto mb-4 opacity-50"
              />
              <p className="text-[#5C5C5C]">{t('noResults')}</p>
            </div>
          )}
        </div>
      </section>

      {/* Privātie sludinājumi (C2C classifieds) */}
      {featuredClassifieds.length > 0 && (
        <section className="py-16 bg-white" data-testid="home-classifieds-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#E6F2F0] flex items-center justify-center text-xl">🧳</div>
                <h2 className="font-outfit text-2xl sm:text-3xl font-bold text-[#121212]">
                  {pick(language, 'Privātie sludinājumi', 'Classifieds', 'Частные объявления')}
                </h2>
              </div>
              <Link to="/sludinajumi" className="flex items-center gap-1 text-[#2F7E79] hover:text-[#276b66] font-medium transition-colors" data-testid="view-all-classifieds-link">
                {pick(language, 'Skatīt visus', 'View all', 'Все')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {featuredClassifieds.slice(0, 8).map((it) => (
                <ClassifiedCard key={it.id} item={it} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
